// PurchaseSimulation2View.js — 구매 시뮬레이션 ② (카테고리별 3개월 수급 시뮬레이션)
//
// 카테고리(상품 묶음)를 골라 엑셀 '구매시뮬레이션' 시트와 같은 표로 보여주고,
// 소요량·발주량을 고쳐가며 석 달 뒤 기말재고를 확인한 뒤 엑셀로 내보낸다.
//
//   M1 = 기준월(기본: 다음달), M2 = 그 다음달, M3 = 그 다다음달 (예: 11월·12월·1월)
//   M1 소요량   = 월 사용량(발주 산출의 가중 일평균×30, 없으면 최근 30일 판매) — 수정 가능
//   M1 발주량   = M1에 새로 발주하는 수량(기본 0) — 수정 가능
//   M1 말 재고  = 현재고 + 발주잔량 + M1 발주량 − M1 소요량
//   M2 소요량   = M1 소요량을 그대로 가져옴 — 수정 가능
//   M2 권장발주 = M2 소요량 + 목표 기말재고 − M1 말 재고 (0 미만이면 0, 발주 단위로 올림) — 수정 가능
//                 목표 기말재고 = M2 소요량 ÷ 30 × (리드타임 + 여유일수)
//                 → 다음 발주가 들어올 때까지(리드타임) 버틸 재고를 M2 말에 남긴다.
//   M2 말 재고  = M1 말 재고 − M2 소요량 + M2 발주량
//   M3 소요량   = M2 소요량을 그대로 가져옴 — 수정 가능
//   M3 권장발주 = M3 소요량 + M3 소요량 ÷ 30 × (리드타임 + 여유일수) − M2 말 재고 (0 미만이면 0, 발주 단위로 올림) — 수정 가능
//   M3 말 재고  = M2 말 재고 − M3 소요량 + M3 발주량
//
// 카테고리 엑셀 업로드: '상품코드'·'상품명'만 있으면 카테고리 1개, '카테고리'(번호)·'카테고리명' 열이 있으면 여러 개를 한 번에 추가한다.
// 카테고리 목록은 Firestore shared/simCategories 문서에 저장(로그인=읽기, 관리자=쓰기 — 기존 규칙 그대로).
// 문서가 없으면 코드에 내장된 '1. 니트릴 장갑'을 기본값으로 쓴다.
// 표에서 고친 값(소요량·발주량)은 브라우저(localStorage)에만 임시 저장되며 앱 데이터에는 반영되지 않는다.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Layers, FileSpreadsheet, RotateCcw, Upload, Trash2, AlertTriangle } from "lucide-react";
import XLSXStyle from "xlsx-js-style";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "./firebase";

const CAT_DOC = doc(db, "shared", "simCategories");
// 구매계획(발주 앱)의 납기별 발주잔량을 월별 입고량으로 나눈다.
// pre = M1 시작 전 입고(이미 지난 납기·날짜 미입력 잔량 포함), n1~n3 = M1~M3 안에 납기인 물량, beyond = M3 이후 납기
const bucketInbound = (inb, total, bd) => {
  const r = { pre: 0, n1: 0, n2: 0, n3: 0, beyond: 0 };
  let dated = 0;
  Object.keys(inb || {}).forEach((d) => {
    const q = toNum(inb[d]);
    if (!q) return;
    dated += q;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || d <= bd.preEnd) r.pre += q;
    else if (d <= bd.m1End) r.n1 += q;
    else if (d <= bd.m2End) r.n2 += q;
    else if (d <= bd.m3End) r.n3 += q;
    else r.beyond += q;
  });
  r.pre += Math.max(0, toNum(total) - dated);
  return r;
};
const EXCLUDE_CODES = new Set(["S13030"]); // 시뮬레이션 ②에서 제외할 상품코드
const EMPTY_OV = { a: {}, b: {}, e: {}, f: {}, h: {}, i: {}, k: {}, l: {} };
const DRAFT_DOC = doc(db, "shared", "simDrafts"); // { drafts: { "<catId>:<yyyy-m>": JSON문자열 } }

// 기본 카테고리 — 2026-09-22 '니트릴 장갑 구매시뮬레이션' 엑셀의 품목 순서 그대로
const DEFAULT_CATEGORIES = [
  {
    id: "cat1",
    name: "1. 니트릴 장갑",
    items: [
      ["12711", "이너홈 니트릴장갑 화이트 100매 미니"],
      ["12520", "이너홈 니트릴장갑 화이트 100매 소형"],
      ["12519", "이너홈 니트릴장갑 화이트 100매 중형"],
      ["12521", "이너홈 니트릴장갑 화이트 100매 대형"],
      ["13638", "이너홈 니트릴장갑 화이트 100매 특대"],
      ["12712", "이너홈 니트릴장갑 블랙 100매 미니"],
      ["12523", "이너홈 니트릴장갑 블랙 100매 소형"],
      ["12522", "이너홈 니트릴장갑 블랙 100매 중형"],
      ["12524", "이너홈 니트릴장갑 블랙 100매 대형"],
      ["13643", "이너홈 니트릴장갑 블랙 100매 특대"],
      ["12516", "이너홈 니트릴장갑 화이트 200매 소형"],
      ["12517", "이너홈 니트릴장갑 화이트 200매 중형"],
      ["12518", "이너홈 니트릴장갑 화이트 200매 대형"],
      ["12513", "이너홈 니트릴장갑 블랙 200매 소형"],
      ["12514", "이너홈 니트릴장갑 블랙 200매 중형"],
      ["12515", "이너홈 니트릴장갑 블랙 200매 대형"],
      ["13639", "이너홈 니트릴장갑 100매 블루 소형"],
      ["13640", "이너홈 니트릴장갑 100매 블루 중형"],
      ["13641", "이너홈 니트릴장갑 100매 블루 대형"],
      ["13642", "이너홈 니트릴장갑 블루 100매 특대"],
      ["12753", "몽키핸즈 니트릴장갑 200매 화이트 소형"],
      ["12754", "몽키핸즈 니트릴장갑 200매 화이트 중형"],
      ["12755", "몽키핸즈 니트릴장갑 200매 화이트 대형"],
      ["12756", "몽키핸즈 니트릴장갑 200매 블랙 소형"],
      ["12757", "몽키핸즈 니트릴장갑 200매 블랙 중형"],
      ["12758", "몽키핸즈 니트릴장갑 200매 블랙 대형"],
    ].map(([code, name]) => ({ code, name })),
  },
];

const nf = (n) => (Number(n) || 0).toLocaleString("ko-KR");
const toNum = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? 0 : n;
};
const has = (o, k) => o && o[k] != null && o[k] !== "";
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const roundUp = (q, unit) => (unit > 1 ? Math.ceil(q / unit) * unit : Math.ceil(q));
const lsGet = (k) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch (e) { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* 저장 불가 환경은 무시 */ } };

export default function PurchaseSimulation2View({
  onClose, items = [], reorderRows = [], rawStock = [], leadTimes = {},
  canon = (c) => String(c || "").trim(), isAdmin = false,
}) {
  const [cats, setCats] = useState(DEFAULT_CATEGORIES);
  const [catSource, setCatSource] = useState("기본값");
  const [catId, setCatId] = useState(() => lsGet("sim2:cat") || "cat1");
  const [monthOffset, setMonthOffset] = useState(1); // 1 = 다음달부터
  const [extraDays, setExtraDays] = useState(0);
  const [unit, setUnit] = useState(100);
  const [ltDefault, setLtDefault] = useState(45); // ② 전용 기본 리드타임(일) — 발주 산출 표에 품목별 리드타임을 적은 품목은 그 값 우선
  const [ov, setOv] = useState(EMPTY_OV);
  const [newName, setNewName] = useState("");
  const [note, setNote] = useState(null);

  const flash = (text, kind = "info") => { setNote({ text, kind }); setTimeout(() => setNote(null), 4000); };

  // 카테고리 목록 실시간 구독
  useEffect(() => {
    const unsub = onSnapshot(CAT_DOC, (snap) => {
      const list = snap.exists() && Array.isArray(snap.data().list) ? snap.data().list : null;
      if (list && list.length) { setCats(list); setCatSource("클라우드"); }
      else { setCats(DEFAULT_CATEGORIES); setCatSource("기본값"); }
    }, () => { setCats(DEFAULT_CATEGORIES); setCatSource("기본값"); });
    return () => unsub();
  }, []);

  const cat = cats.find((c) => c.id === catId) || cats[0];

  // 기준월 라벨
  const months = useMemo(() => {
    const t = new Date();
    const m1 = new Date(t.getFullYear(), t.getMonth() + monthOffset, 1);
    const m2 = new Date(t.getFullYear(), t.getMonth() + monthOffset + 1, 1);
    const m2End = new Date(t.getFullYear(), t.getMonth() + monthOffset + 2, 0);
    const m3 = new Date(t.getFullYear(), t.getMonth() + monthOffset + 2, 1);
    const m3End = new Date(t.getFullYear(), t.getMonth() + monthOffset + 3, 0);
    return { preEnd: ymd(new Date(t.getFullYear(), t.getMonth() + monthOffset, 0)), m1End: ymd(new Date(t.getFullYear(), t.getMonth() + monthOffset + 1, 0)), m0: `${t.getMonth() + 1}월`, hasM0: monthOffset === 1, m1: `${m1.getMonth() + 1}월`, m2: `${m2.getMonth() + 1}월`, m3: `${m3.getMonth() + 1}월`, m2End: ymd(m2End), m3End: ymd(m3End), key: `${m1.getFullYear()}-${m1.getMonth() + 1}` };
  }, [monthOffset]);

  const draftKey = cat ? `sim2:draft:${cat.id}:${months.key}` : "";
  const cloudKey = cat ? `${cat.id}:${months.key}` : "";
  const [cloudDrafts, setCloudDrafts] = useState(null);
  const [saveState, setSaveState] = useState("");
  const hydratedKey = useRef("");
  useEffect(() => {
    const unsub = onSnapshot(DRAFT_DOC, (snap) => setCloudDrafts(snap.exists() && snap.data().drafts ? snap.data().drafts : {}), () => setCloudDrafts({}));
    return () => unsub();
  }, []);
  // 카테고리/기준월이 바뀌면 클라우드 저장본(없으면 이 브라우저 임시본)을 불러옴
  useEffect(() => {
    if (!draftKey || cloudDrafts === null || hydratedKey.current === cloudKey) return;
    let saved = null;
    try { if (cloudDrafts[cloudKey]) saved = JSON.parse(cloudDrafts[cloudKey]); } catch (e) { saved = null; }
    if (saved && saved.ov) {
      setOv({ ...EMPTY_OV, ...saved.ov });
      if (saved.extraDays != null) setExtraDays(saved.extraDays);
      if (saved.unit != null) setUnit(saved.unit);
      if (saved.ltDefault != null) setLtDefault(saved.ltDefault);
    } else setOv({ ...EMPTY_OV, ...(lsGet(draftKey) || {}) });
    hydratedKey.current = cloudKey;
  }, [draftKey, cloudKey, cloudDrafts]);
  useEffect(() => { if (cat) lsSet("sim2:cat", cat.id); }, [cat]);
  // 수정값·설정이 바뀌면 관리자는 자동으로 클라우드에 저장
  useEffect(() => {
    if (!isAdmin || !cloudKey || hydratedKey.current !== cloudKey) return;
    const t = setTimeout(async () => {
      try {
        setSaveState("저장 중…");
        await setDoc(DRAFT_DOC, { drafts: { [cloudKey]: JSON.stringify({ ov, extraDays, unit, ltDefault, at: Date.now() }) } }, { merge: true });
        setSaveState("저장됨");
      } catch (e) { setSaveState("저장 실패"); }
    }, 800);
    return () => clearTimeout(t);
  }, [ov, extraDays, unit, ltDefault, isAdmin, cloudKey]);
  const setCell = (col, code, val) => {
    const next = { ...ov, [col]: { ...ov[col], [code]: val } };
    if (val === "") delete next[col][code];
    setOv(next); lsSet(draftKey, next);
  };
  const resetDraft = () => { setOv(EMPTY_OV); lsSet(draftKey, EMPTY_OV); };

  // 시스템 데이터 인덱스 (상품코드 → 현재고·발주잔량·월사용량·리드타임)
  const src = useMemo(() => {
    const m = new Map();
    (rawStock || []).forEach((s) => {
      const c = canon(s.code);
      const p = m.get(c) || { name: s.name || "", stock: 0, order: 0, monthly: 0, lt: null, inbound: {} };
      p.stock += toNum(s.stock); if (!p.name) p.name = s.name || "";
      m.set(c, p);
    });
    items.forEach((it) => {
      const c = String(it.code);
      const p = m.get(c) || { name: "", stock: 0, order: 0, monthly: 0, lt: null, inbound: {} };
      m.set(c, { ...p, name: it.name || p.name, stock: toNum(it.stock), order: toNum(it.order), monthly: toNum(it.monthly), inbound: it.inbound || {} });
    });
    reorderRows.forEach((r) => {
      const c = String(r.code);
      const p = m.get(c) || { name: r.name || "", stock: toNum(r.stock), order: toNum(r.order), monthly: 0, lt: null, inbound: {} };
      m.set(c, { ...p, monthly: r.dAvg > 0 ? r.dAvg * 30 : p.monthly });
    });
    return m;
  }, [items, reorderRows, rawStock, canon]);

  const rows = useMemo(() => {
    if (!cat) return [];
    const today = new Date();
    const daysLeft = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() - today.getDate() + 1; // 오늘 포함 이번달 잔여일
    return (cat.items || []).filter((ci) => !EXCLUDE_CODES.has(String(ci.code).trim())).map((ci) => {
      const code = canon(ci.code);
      const s = src.get(code);
      const stock = s ? s.stock : 0;
      const order = s ? s.order : 0;
      const bk = bucketInbound(s ? s.inbound : null, order, months);
      const n1 = bk.n1, n2 = bk.n2, n3 = bk.n3;
      const baseF = Math.max(0, Math.round(s ? s.monthly : 0));
      const lt = Math.max(1, toNum(leadTimes[code]) || toNum(ltDefault) || 45);
      const f = has(ov.f, code) ? Math.max(0, toNum(ov.f[code])) : baseF;
      // M1 신규 발주량(기본 0) — 기존 발주잔량과 별개로 M1에 추가로 들어올 물량
      const e = has(ov.e, code) ? Math.max(0, toNum(ov.e[code])) : 0;
      // M0(이번달) — 오늘~말일 잔여 소요량, 이번달 입고되는 발주잔량, 이번달말 재고. 기준월이 '다음달부터'일 때만 사용
      const a = has(ov.a, code) ? Math.max(0, toNum(ov.a[code])) : Math.round((baseF / 30) * daysLeft);
      const b = has(ov.b, code) ? Math.max(0, toNum(ov.b[code])) : bk.pre;
      const c = stock - a + b;
      const g = months.hasM0 ? c + n1 + e - f : stock + b + n1 + e - f;
      const h = has(ov.h, code) ? Math.max(0, toNum(ov.h[code])) : f;
      const targetEnd = (h / 30) * (lt + toNum(extraDays));
      const sug = Math.max(0, roundUp(h + targetEnd - g - n2, unit));
      const i = has(ov.i, code) ? Math.max(0, toNum(ov.i[code])) : sug;
      const j = g + n2 - h + i;
      // M3 — M2말 재고에서 이어서 계산 (소요량 기본 = M2 소요량)
      const k = has(ov.k, code) ? Math.max(0, toNum(ov.k[code])) : h;
      const targetEnd3 = (k / 30) * (lt + toNum(extraDays));
      const sug3 = Math.max(0, roundUp(k + targetEnd3 - j - n3, unit));
      const l = has(ov.l, code) ? Math.max(0, toNum(ov.l[code])) : sug3;
      const m = j + n3 - k + l;
      const arr = new Date(today.getTime() + lt * 86400000);
      const arrival = ymd(arr);
      const lateM2 = i > 0 && arrival > months.m2End;
      const lateM3 = l > 0 && arrival > months.m3End;
      return {
        code, name: (s && s.name) || ci.name || "", found: !!s, stock, order, a, b, bAuto: bk.pre, c, n1, n2, n3, beyond: bk.beyond, e, baseF, f, g, h, sug, i, j, k, sug3, l, m, lt, arrival,
        lateM2, lateM3, late: lateM2 || lateM3,
        edited: has(ov.a, code) || has(ov.b, code) || has(ov.e, code) || has(ov.f, code) || has(ov.h, code) || has(ov.i, code) || has(ov.k, code) || has(ov.l, code),
      };
    });
  }, [cat, src, ov, leadTimes, ltDefault, extraDays, unit, months, canon]);

  const tot = useMemo(() => rows.reduce((a, r) => {
    ["stock", "baseF", "order", "a", "b", "c", "n1", "n2", "n3", "e", "f", "g", "h", "i", "j", "k", "l", "m"].forEach((key) => { a[key] += r[key]; });
    return a;
  }, { stock: 0, baseF: 0, order: 0, a: 0, b: 0, c: 0, n1: 0, n2: 0, n3: 0, e: 0, f: 0, g: 0, h: 0, i: 0, j: 0, k: 0, l: 0, m: 0 }), [rows]);
  const shortCount = rows.filter((r) => r.j < 0).length;
  const shortCount3 = rows.filter((r) => r.m < 0).length;
  const lateCount = rows.filter((r) => r.late).length;
  const daysLeftNow = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() - new Date().getDate() + 1;
  const missing = rows.filter((r) => !r.found).length;

  // ===== 카테고리 업로드 (관리자) =====
  const saveCats = async (list) => {
    await setDoc(CAT_DOC, { list, updatedAt: Date.now() });
  };
  const onUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!isAdmin) { flash("카테고리 업로드는 관리자만 가능합니다.", "error"); return; }
    try {
      const wb = XLSXStyle.read(await file.arrayBuffer(), { type: "array" });
      const aoa = XLSXStyle.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "" });
      const hi = aoa.findIndex((r) => r.some((v) => String(v).replace(/\s/g, "") === "상품코드"));
      if (hi < 0) throw new Error("'상품코드' 머리글을 찾지 못했습니다.");
      const hdr = aoa[hi].map((v) => String(v).replace(/\s/g, ""));
      const ci = hdr.indexOf("상품코드"), ni = hdr.indexOf("상품명");
      const ki = hdr.indexOf("카테고리"), kn = hdr.indexOf("카테고리명");
      if (ki >= 0) {
        // '카테고리'(번호) 열이 있으면 한 파일로 여러 카테고리를 한 번에 만든다. '카테고리명' 열이 있으면 "번호. 이름"으로 저장.
        const groups = new Map();
        aoa.slice(hi + 1).forEach((r) => {
          const code = String(r[ci] ?? "").trim();
          const g = String(r[ki] ?? "").trim();
          if (!g || !/^[A-Za-z]?\d{3,}$/.test(code)) return; // 카테고리 미지정·설명행 제외
          if (!groups.has(g)) groups.set(g, { label: "", items: [], seen: new Set() });
          const G = groups.get(g);
          if (kn >= 0 && !G.label) G.label = String(r[kn] ?? "").trim();
          if (G.seen.has(code)) return;
          G.seen.add(code);
          G.items.push({ code, name: ni >= 0 ? String(r[ni] ?? "").trim() : "" });
        });
        if (!groups.size) throw new Error("카테고리가 지정된 상품 행이 없습니다.");
        let next = [...cats];
        let firstId = null, added = 0, updated = 0;
        [...groups.entries()].sort((a, b) => toNum(a[0]) - toNum(b[0])).forEach(([g, G]) => {
          const label = G.label || "카테고리";
          const gname = /^\d+\s*\./.test(label) ? label : `${g}. ${label}`;
          const same = next.find((c) => c.name === gname);
          const gid = same ? same.id : `cat${Date.now()}_${g}`;
          next = same ? next.map((c) => (c.id === gid ? { ...c, items: G.items } : c)) : [...next, { id: gid, name: gname, items: G.items }];
          if (!firstId) firstId = gid;
          if (same) updated += 1; else added += 1;
        });
        await saveCats(next);
        setCatId(firstId);
        flash(`카테고리 ${added}개 추가 · ${updated}개 갱신 완료`, "success");
        return;
      }
      const seen = new Set();
      const list = [];
      aoa.slice(hi + 1).forEach((r) => {
        const code = String(r[ci] ?? "").trim();
        if (!/^[A-Za-z]?\d{3,}$/.test(code) || seen.has(code)) return; // 설명행·합계행·중복 제외
        seen.add(code);
        list.push({ code, name: ni >= 0 ? String(r[ni] ?? "").trim() : "" });
      });
      if (!list.length) throw new Error("상품코드가 있는 행이 없습니다.");
      const name = newName.trim() || `${cats.length + 1}. ${file.name.replace(/\.[^.]+$/, "").replace(/_?구매시뮬레이션.*$/, "").trim()}`;
      const same = cats.find((c) => c.name === name);
      const id = same ? same.id : `cat${Date.now()}`;
      const next = same ? cats.map((c) => (c.id === id ? { ...c, items: list } : c)) : [...cats, { id, name, items: list }];
      await saveCats(next);
      setCatId(id); setNewName("");
      flash(`'${name}' ${list.length}품목 ${same ? "갱신" : "추가"} 완료`, "success");
    } catch (err) { flash("업로드 실패: " + err.message, "error"); }
  };
  const removeCat = async () => {
    if (!isAdmin || !cat) return;
    if (cats.length <= 1) { flash("마지막 카테고리는 삭제할 수 없습니다.", "error"); return; }
    if (!window.confirm(`'${cat.name}' 카테고리를 삭제할까요?`)) return;
    try { const next = cats.filter((c) => c.id !== cat.id); await saveCats(next); setCatId(next[0].id); flash("삭제했습니다.", "success"); }
    catch (err) { flash("삭제 실패: " + err.message, "error"); }
  };

  // ===== 엑셀 내보내기 — 원본 시트와 같은 열 구성, 재고 열은 수식으로 =====
  const exportXlsx = () => {
    try {
      const H = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "1E293B" } }, alignment: { horizontal: "center", vertical: "center", wrapText: true }, border: { bottom: { style: "thin", color: { rgb: "94A3B8" } } } };
      const N = { numFmt: "#,##0;[Red]-#,##0" };
      const E = { ...N, fill: { fgColor: { rgb: "FEF9C3" } } }; // 수정 가능 열
      const C = { ...N, fill: { fgColor: { rgb: "E0F2FE" } } }; // 수식 열
      const m0 = months.hasM0;
      const spec = [
        { k: "code", h: "상품코드", w: 9, str: true },
        { k: "name", h: "상품명", w: 36, str: true },
        { k: "stock", h: "현재고", w: 9, s: N, tot: "stock" },
        { k: "baseF", h: "월평균 소요량", w: 11, s: N, tot: "baseF" },
        ...(m0 ? [
          { k: "a", h: `${months.m0} 소요량(잔여)`, w: 11, s: E, tot: "a" },
          { k: "b", h: `${months.m0} 발주잔량(입고)`, w: 11, s: E, tot: "b" },
          { k: "c", h: `${months.m0}말 재고`, w: 11, s: C, tot: "c", f: (L, R) => `${L.stock}${R}-${L.a}${R}+${L.b}${R}` },
        ] : [{ k: "b", h: `${months.m1} 이전 입고`, w: 10, s: E, tot: "b" }]),
        { k: "n1", h: `${months.m1} 입고(기존발주)`, w: 12, s: N, tot: "n1" },
        { k: "e", h: `${months.m1} 발주량`, w: 10, s: E, tot: "e" },
        { k: "f", h: `${months.m1} 소요량`, w: 10, s: E, tot: "f" },
        { k: "g", h: `${months.m1}말 재고`, w: 10, s: C, tot: "g", f: (L, R) => (m0 ? `${L.c}${R}+${L.n1}${R}+${L.e}${R}-${L.f}${R}` : `${L.stock}${R}+${L.b}${R}+${L.n1}${R}+${L.e}${R}-${L.f}${R}`) },
        { k: "n2", h: `${months.m2} 입고(기존발주)`, w: 12, s: N, tot: "n2" },
        { k: "h", h: `${months.m2} 소요량`, w: 10, s: E, tot: "h" },
        { k: "i", h: `${months.m2} 발주량`, w: 10, s: E, tot: "i" },
        { k: "j", h: `${months.m2}말 재고`, w: 10, s: C, tot: "j", f: (L, R) => `${L.g}${R}+${L.n2}${R}-${L.h}${R}+${L.i}${R}` },
        { k: "n3", h: `${months.m3} 입고(기존발주)`, w: 12, s: N, tot: "n3" },
        { k: "k", h: `${months.m3} 소요량`, w: 10, s: E, tot: "k" },
        { k: "l", h: `${months.m3} 발주량`, w: 10, s: E, tot: "l" },
        { k: "m", h: `${months.m3}말 재고`, w: 10, s: C, tot: "m", f: (L, R) => `${L.j}${R}+${L.n3}${R}-${L.k}${R}+${L.l}${R}` },
        { k: "lt", h: "리드타임(일)", w: 9, s: N },
        { k: "arrival", h: "입고예정일", w: 11, str: true },
        { k: "sug", h: `${months.m2} 권장발주량`, w: 10, s: N },
        { k: "sug3", h: `${months.m3} 권장발주량`, w: 10, s: N },
      ];
      const L = {};
      spec.forEach((col, n) => { L[col.k] = XLSXStyle.utils.encode_col(n); });
      const ws = {};
      const put = (c, r, cell) => { ws[XLSXStyle.utils.encode_cell({ c, r })] = cell; };
      spec.forEach((col, c) => put(c, 0, { v: col.h, t: "s", s: H }));
      rows.forEach((x, k) => {
        const r = k + 1, R = r + 1;
        spec.forEach((col, c) => {
          if (col.str) put(c, r, { v: x[col.k], t: "s" });
          else put(c, r, col.f ? { v: x[col.k], t: "n", f: col.f(L, R), s: col.s } : { v: x[col.k], t: "n", s: col.s });
        });
      });
      const last = rows.length + 1, sr = rows.length + 1;
      const B = { font: { bold: true }, fill: { fgColor: { rgb: "F1F5F9" } }, numFmt: "#,##0;[Red]-#,##0", border: { top: { style: "thin", color: { rgb: "94A3B8" } } } };
      put(0, sr, { v: "합계", t: "s", s: B });
      put(1, sr, { v: `${rows.length}품목`, t: "s", s: B });
      spec.forEach((col, c) => { if (col.tot) put(c, sr, { v: tot[col.tot], t: "n", f: `SUM(${L[col.k]}2:${L[col.k]}${last})`, s: B }); });
      ws["!ref"] = XLSXStyle.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: spec.length - 1, r: sr } });
      ws["!cols"] = spec.map((col) => ({ wch: col.w }));
      ws["!rows"] = [{ hpt: 30 }];
      const info = XLSXStyle.utils.aoa_to_sheet([
        ["카테고리", cat ? cat.name : ""],
        ["작성일", ymd(new Date())],
        ["기준월", `${months.hasM0 ? months.m0 + "(이번달) → " : ""}${months.m1} ~ ${months.m3}`],
        ["발주 단위", unit],
        ["여유일수", toNum(extraDays)],
        [],
        ["열 설명"],
        ...(months.hasM0 ? [[`${months.m0} 소요량(잔여)`, "월 사용량÷30 × 오늘 포함 이번달 잔여일수. 노란 열은 수정값"], [`${months.m0} 발주잔량`, "구매계획 납기가 이번달 이전·이번달인 발주잔량 + 납기 미입력 잔량 (수정 가능)"], [`${months.m1}·${months.m2}·${months.m3} 입고(기존발주)`, "구매계획 납기(date)가 해당월인 발주잔량. 납기가 M3 이후인 물량은 반영하지 않음"], [`${months.m0}말 재고`, "현재고 − 잔여 소요량 + 발주잔량 (수식)"]] : []),
        [`${months.m1}말 재고`, months.hasM0 ? `${months.m0}말 재고 + 입고 + 발주량 − 소요량 (수식)` : "현재고 + 이전 입고 + 입고 + 발주량 − 소요량 (수식)"],
        [`${months.m2} 발주량`, "권장 = 소요량 + 소요량÷30×(리드타임+여유일수) − 전월말 재고, 발주 단위로 올림. 노란 열은 수정값"],
        [`${months.m2}말 재고`, "전월말 재고 − 소요량 + 발주량 (수식)"],
        [`${months.m3} 발주량`, `권장 = 소요량 + 소요량÷30×(리드타임+여유일수) − ${months.m2}말 재고, 발주 단위로 올림. 노란 열은 수정값`],
        [`${months.m3}말 재고`, `${months.m2}말 재고 − ${months.m3} 소요량 + ${months.m3} 발주량 (수식)`],
      ]);
      info["!cols"] = [{ wch: 14 }, { wch: 70 }];
      const wb = XLSXStyle.utils.book_new();
      XLSXStyle.utils.book_append_sheet(wb, ws, "구매시뮬레이션");
      XLSXStyle.utils.book_append_sheet(wb, info, "설정");
      const safe = (cat ? cat.name : "카테고리").replace(/^\d+\.\s*/, "").replace(/[\\/:*?"<>|]/g, "");
      XLSXStyle.writeFile(wb, `${safe}_구매시뮬레이션_${ymd(new Date())}.xlsx`);
    } catch (err) { flash("엑셀 내보내기 실패: " + err.message, "error"); }
  };

  const inp = (col, r, val, tone) => (
    <input type="number" min={0} value={has(ov[col], r.code) ? ov[col][r.code] : ""} placeholder={nf(val)}
      onChange={(e) => setCell(col, r.code, e.target.value)}
      className={"w-full rounded-md border bg-white px-1.5 py-0.5 text-right text-xs tabular-nums placeholder:text-slate-500 focus:outline-none focus:ring-2 " + tone} />
  );
  const nCell = (v, label, r) => (
    <td className="px-2 py-1.5 text-right tabular-nums bg-amber-50/60 text-amber-800" title={r.beyond ? `${label} · M3 이후 납기 ${nf(r.beyond)}는 반영 안 됨` : label}>{v ? nf(v) : <span className="text-slate-300">—</span>}</td>
  );
  const neg = (n) => (n < 0 ? "text-rose-600 font-bold" : "text-slate-800");

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-slate-50">
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <Layers className="h-5 w-5 text-indigo-600" />
        <h2 className="text-base font-bold text-slate-800">구매 시뮬레이션 ②</h2>
        <span className="text-xs text-slate-400">카테고리별 {months.m1}·{months.m2}·{months.m3} 수급 시뮬레이션 {isAdmin ? `(수정값·설정 자동 클라우드 저장${saveState ? " · " + saveState : ""})` : "(저장된 값을 보는 중 · 수정은 이 브라우저에만 임시 저장)"}</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={resetDraft} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><RotateCcw className="h-3.5 w-3.5" />수정값 초기화</button>
          <button onClick={exportXlsx} disabled={!rows.length} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1 text-[11px] font-medium text-white hover:bg-emerald-800 disabled:opacity-40"><FileSpreadsheet className="h-3.5 w-3.5" />엑셀 내보내기</button>
          <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><X className="h-3.5 w-3.5" />닫기</button>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] p-5">
        {note && (
          <div className={"mb-3 rounded-lg px-3 py-2 text-xs " + (note.kind === "error" ? "bg-rose-50 text-rose-700" : note.kind === "success" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600")}>{note.text}</div>
        )}

        {/* 카테고리 선택 */}
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <span className="text-xs font-semibold text-slate-500">카테고리</span>
          {cats.map((c) => (
            <button key={c.id} onClick={() => setCatId(c.id)}
              className={"rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors " + (cat && c.id === cat.id ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
              {c.name} <span className="font-normal opacity-70">({(c.items || []).length})</span>
            </button>
          ))}
          <span className="text-[11px] text-slate-400">· {catSource}</span>
          {isAdmin && (
            <div className="ml-auto flex items-center gap-2">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="새 카테고리명 (비우면 파일명)"
                className="w-52 rounded-lg border border-slate-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300" />
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                title="첫 시트에서 '상품코드'·'상품명' 머리글을 찾아 읽습니다. '카테고리'(번호)·'카테고리명' 열이 있으면 여러 카테고리를 한 번에 만듭니다. 같은 이름이 있으면 품목을 교체합니다.">
                <Upload className="h-3.5 w-3.5" />카테고리 엑셀 업로드
                <input type="file" accept=".xlsx,.xls" onChange={onUpload} className="hidden" />
              </label>
              <button onClick={removeCat} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-500 hover:bg-rose-50 hover:text-rose-600" title="현재 카테고리 삭제"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>

        {/* 설정 */}
        <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
          <label className="flex items-center gap-1.5">기준월
            <select value={monthOffset} onChange={(e) => setMonthOffset(Number(e.target.value))} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs">
              <option value={0}>이번달부터</option><option value={1}>다음달부터</option><option value={2}>다다음달부터</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">발주 단위
            <select value={unit} onChange={(e) => setUnit(Number(e.target.value))} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs">
              {[1, 100, 500, 1000].map((u) => <option key={u} value={u}>{u === 1 ? "1개" : nf(u) + "개"}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-violet-700">기본 리드타임
            <input type="number" min={1} value={ltDefault} onChange={(e) => setLtDefault(e.target.value)} className="w-16 rounded-lg border border-violet-200 bg-violet-50 px-2 py-1 text-right text-xs tabular-nums" />일
          </label>
          <label className="flex items-center gap-1.5">여유일수
            <input type="number" value={extraDays} onChange={(e) => setExtraDays(e.target.value)} className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-right text-xs tabular-nums" />일
          </label>
          <span className="text-slate-400">· 권장 {months.m2}·{months.m3} 발주량 = 해당월 소요량 + 소요량÷30×(리드타임+여유일수) − 전월말 재고 · 노란 칸은 직접 수정 가능(비우면 자동값)</span>
        </div>

        {/* 요약 */}
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-8">
          {[
            ["품목", nf(rows.length), missing ? `시스템에 없는 코드 ${missing}` : "모두 매칭", "text-slate-700"],
            ...(months.hasM0 ? [[`${months.m0}말 재고`, nf(tot.c), `잔여 소요 ${nf(tot.a)} · 입고 ${nf(tot.b)}`, tot.c < 0 ? "text-rose-600" : "text-slate-700"]] : []),
            [`${months.m1}말 재고`, nf(tot.g), `소요 ${nf(tot.f)}`, tot.g < 0 ? "text-rose-600" : "text-slate-700"],
            [`${months.m2} 발주량`, nf(tot.i), `권장 합계 ${nf(rows.reduce((a, r) => a + r.sug, 0))}`, "text-orange-600"],
            [`${months.m2}말 재고`, nf(tot.j), shortCount ? `결품 ${shortCount}품목` : "결품 없음", tot.j < 0 || shortCount ? "text-rose-600" : "text-emerald-700"],
            [`${months.m3} 발주량`, nf(tot.l), `권장 합계 ${nf(rows.reduce((a, r) => a + r.sug3, 0))}`, "text-orange-600"],
            [`${months.m3}말 재고`, nf(tot.m), shortCount3 ? `결품 ${shortCount3}품목` : "결품 없음", tot.m < 0 || shortCount3 ? "text-rose-600" : "text-emerald-700"],
            ["입고 지연 위험", `${lateCount}건`, "발주월 말일 이후 입고", lateCount ? "text-amber-600" : "text-slate-700"],
          ].map(([l, v, s, c]) => (
            <div key={l} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-[11px] font-medium text-slate-400">{l}</div>
              <div className={"mt-0.5 text-lg font-bold tabular-nums " + c}>{v}</div>
              <div className="text-[11px] text-slate-400">{s}</div>
            </div>
          ))}
        </div>

        {/* 표 전용 스크롤 영역 — 아래로 내려도 머리글(메뉴)·합계 줄이 고정되어 보인다 */}
        <div className="max-h-[calc(100vh-120px)] overflow-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[2200px] border-separate border-spacing-0 text-xs">
            <thead className="sticky top-0 z-10 shadow-[0_1px_0_0_#cbd5e1]">
              <tr className="text-[10px] font-bold text-white">
                <th className="sticky left-0 z-20 bg-slate-800 px-2 py-2 text-left w-[64px] min-w-[64px] max-w-[64px]">상품코드</th>
                <th className="sticky left-[64px] z-20 bg-slate-800 px-2 py-2 text-center w-[280px] min-w-[280px] max-w-[280px] shadow-[1px_0_0_0_#94a3b8]">상품명</th>
                <th className="bg-amber-700 px-2 py-2 text-right w-[80px]">현재고</th>
                <th className="bg-sky-900 px-2 py-2 text-right w-[96px]">월평균 소요량</th>
                {months.hasM0 ? (<>
                  <th className="bg-sky-800 px-2 py-2 text-right w-[96px]">{months.m0} 소요량(잔여)</th>
                  <th className="bg-amber-700 px-2 py-2 text-right w-[96px]">{months.m0} 발주잔량</th>
                  <th className="bg-slate-600 px-2 py-2 text-right w-[88px]">{months.m0}말 재고</th>
                </>) : (
                  <th className="bg-amber-700 px-2 py-2 text-right w-[96px]">{months.m1} 이전 입고</th>
                )}
                <th className="bg-amber-600 px-2 py-2 text-right w-[88px]">{months.m1} 입고(기존발주)</th>
                <th className="bg-orange-600 px-2 py-2 text-right w-[100px]">{months.m1} 발주량</th>
                <th className="bg-sky-800 px-2 py-2 text-right w-[96px]">{months.m1} 소요량</th>
                <th className="bg-slate-600 px-2 py-2 text-right w-[88px]">{months.m1}말 재고</th>
                <th className="bg-amber-600 px-2 py-2 text-right w-[88px]">{months.m2} 입고(기존발주)</th>
                <th className="bg-sky-800 px-2 py-2 text-right w-[96px]">{months.m2} 소요량</th>
                <th className="bg-orange-600 px-2 py-2 text-right w-[100px]">{months.m2} 발주량</th>
                <th className="bg-slate-600 px-2 py-2 text-right w-[88px]">{months.m2}말 재고</th>
                <th className="bg-amber-600 px-2 py-2 text-right w-[88px]">{months.m3} 입고(기존발주)</th>
                <th className="bg-sky-800 px-2 py-2 text-right w-[96px]">{months.m3} 소요량</th>
                <th className="bg-orange-600 px-2 py-2 text-right w-[100px]">{months.m3} 발주량</th>
                <th className="bg-slate-600 px-2 py-2 text-right w-[88px]">{months.m3}말 재고</th>
                <th className="bg-violet-800 px-2 py-2 text-right w-[52px]">LT</th>
                <th className="bg-violet-800 px-2 py-2 text-center w-[86px]">입고예정</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className={"group [&>td]:border-b [&>td]:border-slate-100 " + (r.edited ? "bg-yellow-50/50" : "hover:bg-slate-50")}>
                  <td className={"sticky left-0 z-[5] w-[64px] min-w-[64px] max-w-[64px] px-2 py-1.5 font-mono text-[11px] text-slate-500 " + (r.edited ? "bg-yellow-50" : "bg-white group-hover:bg-slate-50")}>{r.code}</td>
                  <td title={r.name || ""} className={"sticky left-[64px] z-[5] w-[280px] min-w-[280px] max-w-[280px] truncate px-2 py-1.5 font-medium text-slate-800 shadow-[1px_0_0_0_#cbd5e1] " + (r.edited ? "bg-yellow-50" : "bg-white group-hover:bg-slate-50")}>
                    {r.name || <span className="text-slate-400">(이름 없음)</span>}
                    {!r.found && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-rose-50 px-1 text-[10px] font-semibold text-rose-600"><AlertTriangle className="h-3 w-3" />시스템에 없음</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-800">{nf(r.stock)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-sky-700 font-medium" title="월 사용량 (발주 산출의 가중 일평균×30, 없으면 최근 30일 판매)">{nf(r.baseF)}</td>
                  {months.hasM0 ? (<>
                    <td className="px-1.5 py-1 bg-yellow-50/60" title={`자동값: 월 사용량 ${nf(r.baseF)} ÷ 30 × 이번달 잔여일`}>{inp("a", r, Math.round((r.baseF / 30) * daysLeftNow), "border-yellow-300 focus:ring-yellow-300")}</td>
                    <td className="px-1.5 py-1 bg-yellow-50/60" title={`자동값: 이번달 납기 + 납기 지난 건 + 납기 미입력 잔량 (구매계획 기준)`}>{inp("b", r, r.bAuto, "border-yellow-300 focus:ring-yellow-300")}</td>
                    <td className={"px-2 py-1.5 text-right tabular-nums bg-sky-50/50 " + neg(r.c)}>{nf(r.c)}</td>
                  </>) : (
                    <td className="px-1.5 py-1 bg-yellow-50/60" title={`자동값: ${months.m1} 시작 전 납기 + 납기 미입력 잔량`}>{inp("b", r, r.bAuto, "border-yellow-300 focus:ring-yellow-300")}</td>
                  )}
                  {nCell(r.n1, `${months.m1} 납기 발주잔량`, r)}
                  <td className="px-1.5 py-1 bg-orange-50/60" title={`${months.m1}에 추가로 발주할 수량 (비우면 0)`}>{inp("e", r, 0, "border-orange-300 focus:ring-orange-300 font-semibold")}</td>
                  <td className="px-1.5 py-1 bg-yellow-50/60" title={`자동값: 월 사용량 ${nf(r.baseF)}`}>{inp("f", r, r.baseF, "border-yellow-300 focus:ring-yellow-300")}</td>
                  <td className={"px-2 py-1.5 text-right tabular-nums bg-sky-50/50 " + neg(r.g)}>{nf(r.g)}</td>
                  {nCell(r.n2, `${months.m2} 납기 발주잔량`, r)}
                  <td className="px-1.5 py-1 bg-yellow-50/60" title={`자동값: ${months.m1} 소요량 ${nf(r.f)}`}>{inp("h", r, r.f, "border-yellow-300 focus:ring-yellow-300")}</td>
                  <td className="px-1.5 py-1 bg-orange-50/60" title={`권장 ${nf(r.sug)}`}>{inp("i", r, r.sug, "border-orange-300 focus:ring-orange-300 font-semibold")}</td>
                  <td className={"px-2 py-1.5 text-right tabular-nums bg-sky-50/50 " + neg(r.j)}>{nf(r.j)}</td>
                  {nCell(r.n3, `${months.m3} 납기 발주잔량`, r)}
                  <td className="px-1.5 py-1 bg-yellow-50/60" title={`자동값: ${months.m2} 소요량 ${nf(r.h)}`}>{inp("k", r, r.h, "border-yellow-300 focus:ring-yellow-300")}</td>
                  <td className="px-1.5 py-1 bg-orange-50/60" title={`권장 ${nf(r.sug3)}`}>{inp("l", r, r.sug3, "border-orange-300 focus:ring-orange-300 font-semibold")}</td>
                  <td className={"px-2 py-1.5 text-right tabular-nums bg-sky-50/50 " + neg(r.m)}>{nf(r.m)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{r.lt}</td>
                  <td className={"px-2 py-1.5 text-center tabular-nums " + (r.late ? "font-semibold text-amber-600" : "text-slate-400")} title={r.late ? `${r.lateM2 ? months.m2 : months.m3}말까지 입고되지 않습니다` : ""}>{r.arrival.slice(5)}{r.late ? " ⚠" : ""}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 z-10 shadow-[0_-1px_0_0_#cbd5e1]">
              <tr className="bg-slate-100 font-bold tabular-nums">
                <td className="sticky left-0 z-20 bg-slate-100 px-2 py-2 text-slate-600 shadow-[1px_0_0_0_#94a3b8]" colSpan={2}>합계 ({nf(rows.length)}품목)</td>
                <td className="px-2 py-2 text-right">{nf(tot.stock)}</td>
                <td className="px-2 py-2 text-right text-sky-700">{nf(tot.baseF)}</td>
                {months.hasM0 ? (<>
                  <td className="px-2 py-2 text-right">{nf(tot.a)}</td>
                  <td className="px-2 py-2 text-right">{nf(tot.b)}</td>
                  <td className={"px-2 py-2 text-right " + neg(tot.c)}>{nf(tot.c)}</td>
                </>) : (
                  <td className="px-2 py-2 text-right">{nf(tot.b)}</td>
                )}
                <td className="px-2 py-2 text-right text-amber-700">{nf(tot.n1)}</td>
                <td className="px-2 py-2 text-right text-orange-700">{nf(tot.e)}</td>
                <td className="px-2 py-2 text-right">{nf(tot.f)}</td>
                <td className={"px-2 py-2 text-right " + neg(tot.g)}>{nf(tot.g)}</td>
                <td className="px-2 py-2 text-right text-amber-700">{nf(tot.n2)}</td>
                <td className="px-2 py-2 text-right">{nf(tot.h)}</td>
                <td className="px-2 py-2 text-right text-orange-700">{nf(tot.i)}</td>
                <td className={"px-2 py-2 text-right " + neg(tot.j)}>{nf(tot.j)}</td>
                <td className="px-2 py-2 text-right text-amber-700">{nf(tot.n3)}</td>
                <td className="px-2 py-2 text-right">{nf(tot.k)}</td>
                <td className="px-2 py-2 text-right text-orange-700">{nf(tot.l)}</td>
                <td className={"px-2 py-2 text-right " + neg(tot.m)}>{nf(tot.m)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
          현재고·발주잔량은 시스템 값(현재고 엑셀·발주계획 앱) · 월 사용량은 발주 산출의 가중 일평균(최근 30/60/90일 50·30·20%)×30, 판매 기록이 없으면 최근 30일 판매량 ·
          리드타임은 발주 산출 표의 품목별 값(없으면 기본 {toNum(ltDefault) || 45}일) · 발주잔량은 구매계획의 납기(없으면 입항예정일) 기준으로 월별 입고에 나눠 반영합니다(납기가 지났거나 미입력인 잔량은 {months.hasM0 ? months.m0 : `${months.m1} 이전`} 입고로 처리, {months.m3} 이후 납기는 제외){months.hasM0 ? ` (${months.m0} 소요량은 오늘 포함 잔여 ${daysLeftNow}일분)` : ""}.
        </p>
      </div>
    </div>
  );
}
